import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { ArrowRight, Loader2, CreditCard, MapPin, Shield, AlertCircle, Landmark, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { ordersAPI, paymentsAPI, windowsAPI, collectionPointsAPI, settingsAPI, suppliersAPI } from '@/lib/api';
import { useUnavailableItems } from '@/hooks/useUnavailableItems';
import { googleMapsLink } from '@/lib/maps';
import { sanitizeCell, isValidSACell } from '@/lib/phone';
import { CellphoneHint } from '@/components/CellphoneHint';
import { toast } from '@/components/ui/sonner';
import { formatUnitSize, formatPack } from '@/lib/units';
import { computeDeliveryBreakdown } from '@/lib/delivery';
import { money, round2 } from '@/lib/format';

export default function CheckoutPage() {
  const { user, loading: authLoading, refreshUser } = useAuth();
  const { items, total, clearCart, currentSupplierId, removeItem } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [windowOpen, setWindowOpen] = useState(null);
  const [collectionPoints, setCollectionPoints] = useState([]);
  const [cpError, setCpError] = useState(false);
  const [supplierMetaMap, setSupplierMetaMap] = useState({});
  const [paymentMethods, setPaymentMethods] = useState({ card: true, eft: false });
  const [selectedMethod, setSelectedMethod] = useState('card');
  const [form, setForm] = useState({
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    collection_point_id: '',
  });
  // Validate cart items against live products. A product deleted or hidden after
  // being added to the cart would hard-fail order creation; flag those so the
  // customer can remove them and check out the rest.
  const { unavailableIds, revalidate } = useUnavailableItems(items);

  useEffect(() => {
    windowsAPI.getActive().then(r => setWindowOpen(r.data?.active)).catch(() => {});
    collectionPointsAPI.list().then(r => setCollectionPoints(r.data)).catch(() => {});
    settingsAPI
      .getPublic()
      .then((r) => {
        const pm = { card: true, eft: false, ...(r.data?.payment_methods || {}) };
        setPaymentMethods(pm);
        // Auto-select the (only) enabled method; default to card if both.
        if (pm.card) setSelectedMethod('card');
        else if (pm.eft) setSelectedMethod('eft');
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    let active = true;
    // Deferred (microtask) so the prefill is off the synchronous effect path.
    Promise.resolve().then(() => {
      if (!active) return;
      setForm(f => ({
        ...f,
        contact_name: f.contact_name || user.name || `${user.first_name || ''} ${user.surname || ''}`.trim(),
        contact_email: f.contact_email || user.email || '',
        contact_phone: f.contact_phone || user.cellphone || user.phone || '',
      }));
    });
    return () => { active = false; };
  }, [user]);

  // Fetch delivery config for EVERY supplier in the cart so the summary mirrors
  // the backend's authoritative per-supplier delivery total (multi-vendor safe).
  const supplierIds = useMemo(
    () => [...new Set(items.map(i => i.supplier_id).filter(Boolean))],
    [items]
  );
  const supplierIdsKey = supplierIds.join(',');
  useEffect(() => {
    if (supplierIds.length === 0) { setSupplierMetaMap({}); return undefined; }
    let active = true;
    Promise.all(
      supplierIds.map(id => suppliersAPI.get(id).then(r => [id, r.data]).catch(() => [id, null]))
    ).then(entries => { if (active) setSupplierMetaMap(Object.fromEntries(entries)); });
    return () => { active = false; };
  }, [supplierIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const { lines: deliveryLines, total: deliveryCharge } = useMemo(
    () => computeDeliveryBreakdown(items, supplierMetaMap),
    [items, supplierMetaMap]
  );
  const deliveryChargeLines = deliveryLines.filter(l => l.amount > 0);
  const afterConfirm = supplierMetaMap[currentSupplierId]?.payment_workflow === 'after_confirmation';
  const selectedPoint = collectionPoints.find(p => p.id === form.collection_point_id);
  const collectionFee = round2(Number(selectedPoint?.collection_fee || 0));
  const grandTotal = round2(total + deliveryCharge + collectionFee);
  const creditBalance = Number(user?.credit_balance || 0);
  const creditToApply = round2(Math.min(creditBalance, grandTotal));
  const payable = round2(grandTotal - creditToApply);
  const fullyCovered = creditToApply > 0 && payable <= 0;
  const moqViolations = items
    .filter(i => Number(i.moq || 0) > 1 && i.quantity < Number(i.moq))
    .map(i => ({
      product_id: i.product_id,
      name: i.name,
      moq: Number(i.moq),
      quantity: i.quantity,
      short_by: Number(i.moq) - i.quantity,
    }));
  const hasUnavailable = unavailableIds.length > 0;

  if (authLoading) return null;
  if (!user) return <Navigate to="/auth" />;
  // Once an order is placed we clear the cart and redirect imperatively. Skip
  // the empty-cart guard in that window so it can't hijack the EFT redirect.
  if (items.length === 0 && !orderPlaced) return <Navigate to="/cart" />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.contact_name || !form.contact_email || !form.contact_phone) {
      toast.error('Please fill in all contact fields');
      return;
    }
    if (!isValidSACell(form.contact_phone)) {
      toast.error('Please enter a valid 10-digit South African cellphone number');
      return;
    }
    if (collectionPoints.length > 0 && !form.collection_point_id) {
      setCpError(true);
      toast.error('Please select a collection point');
      return;
    }
    if (moqViolations.length > 0) {
      toast.error(`Minimum order quantity not met for ${moqViolations.length} item(s)`);
      return;
    }
    if (hasUnavailable) {
      toast.error('Please remove unavailable items before placing your order');
      return;
    }

    setLoading(true);
    try {
      const orderRes = await ordersAPI.create({
        items: items.map(i => ({
          product_id: i.product_id,
          name: i.name,
          price: i.price,
          quantity: i.quantity,
          image_url: i.image_url,
        })),
        total,
        ...form,
        payment_method: selectedMethod,
      });
      const createdOrderId = orderRes.data.id;

      // Account credit may have been applied — refresh the cached user balance.
      refreshUser?.();

      // Mark the order as placed BEFORE clearing the cart so the empty-cart
      // guard above doesn't redirect to /cart and clobber the EFT redirect.
      setOrderPlaced(true);

      // Clear the cart as soon as the order is persisted — the order itself is
      // the source of truth from here. If the customer bails on the payment
      // step, they can resume from /profile.
      clearCart();

      // Fully covered by account credit → already paid server-side, no gateway.
      const created = orderRes.data || {};
      if (created.payment_status === 'paid' ||
          (created.amount_payable !== undefined && Number(created.amount_payable) <= 0)) {
        toast.success('Order placed and paid in full with your account credit!');
        navigate(`/orders/${createdOrderId}`);
        return;
      }

      if (afterConfirm) {
        toast.success("Order placed! We'll confirm it shortly and send you a payment link.");
        navigate(`/orders/${createdOrderId}`);
        return;
      }

      if (selectedMethod === 'eft') {
        toast.success('Order placed! Banking details are on the next page.');
        navigate(`/eft/${createdOrderId}`);
        return;
      }

      const checkoutRes = await paymentsAPI.startPaystackCheckout(createdOrderId);
      const redirectUrl = checkoutRes.data?.redirect_url;
      if (!redirectUrl) {
        toast.error('Could not start payment. Visit your profile to retry.');
        navigate('/profile');
        return;
      }
      window.location.assign(redirectUrl);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = (detail && typeof detail === 'object') ? (detail.message || 'Checkout failed') : (detail || 'Checkout failed');
      const code = (detail && typeof detail === 'object') ? detail.code : null;
      // Safety net: if the server rejects a since-removed product, re-run the
      // cart validation so the offending line gets flagged for removal.
      if (code === 'PRODUCT_UNAVAILABLE' || code === 'PRODUCT_NOT_FOUND') {
        revalidate();
      }
      if (code === 'MISSING_COLLECTION_POINT' || code === 'INVALID_COLLECTION_POINT') {
        setCpError(true);
      }
      toast.error(msg);
      setLoading(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Header */}
        <div className="mb-6 sm:mb-10">
          <div className="inline-flex items-center gap-3 mb-2 sm:mb-3">
            <div className="h-[2px] w-8 bg-gold rounded-full" />
            <p className="font-body text-[10px] sm:text-xs uppercase tracking-[0.25em] text-gold font-bold">Checkout</p>
          </div>
          <h1 className="font-heading text-2xl sm:text-4xl lg:text-5xl font-light text-foreground tracking-tight">
            Complete Your Order
          </h1>
        </div>

        {/* Window Closed Warning */}
        {windowOpen === false && (
          <div className="mb-6 sm:mb-8 p-3 sm:p-4 rounded-xl bg-red-900/20 border border-red-700/30 flex items-center gap-3" data-testid="window-closed-warning">
            <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-400 flex-shrink-0" />
            <p className="font-body text-xs sm:text-sm text-red-400">
              The order window is currently closed. You cannot place orders at this time.
            </p>
          </div>
        )}

        {/* MOQ Warning */}
        {moqViolations.length > 0 && (
          <div className="mb-6 sm:mb-8 p-3 sm:p-4 rounded-xl bg-red-900/20 border border-red-700/30" data-testid="moq-warning">
            <div className="flex items-center gap-3 mb-2">
              <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-400 flex-shrink-0" />
              <p className="font-body text-xs sm:text-sm text-red-400 font-semibold">
                Some items are below their minimum order quantity:
              </p>
            </div>
            <ul className="font-body text-xs sm:text-sm text-red-400/90 list-disc pl-8 space-y-0.5">
              {moqViolations.map(v => (
                <li key={v.product_id}>
                  <strong>{v.name}</strong>: minimum {v.moq} units — you have {v.quantity} ({v.short_by} short).
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-10">
          {/* Contact & Collection Form */}
          <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-6 sm:space-y-8" data-testid="checkout-form">
            <div className="bg-card rounded-xl sm:rounded-2xl p-5 sm:p-8 border border-border">
              <h2 className="font-heading text-xl sm:text-2xl font-medium text-foreground mb-3 sm:mb-4 flex items-center gap-3">
                <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-teal" />
                Contact Details
              </h2>
              <p className="font-body text-xs sm:text-sm text-foreground/50 mb-4 sm:mb-6">
                All orders are for <span className="font-semibold text-foreground/70">collection only</span>.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                <div>
                  <Label className="font-body text-xs sm:text-sm text-foreground/70 font-medium mb-2 block">Full Name</Label>
                  <Input
                    data-testid="checkout-name"
                    value={form.contact_name}
                    onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))}
                    className="rounded-xl bg-muted border-border focus:border-teal focus:ring-2 focus:ring-teal/20 font-body py-5 sm:py-6 text-foreground text-sm"
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div>
                  <Label className="font-body text-xs sm:text-sm text-foreground/70 font-medium mb-2 block">Email Address</Label>
                  <Input
                    data-testid="checkout-email"
                    type="email"
                    value={form.contact_email}
                    onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))}
                    className="rounded-xl bg-muted border-border focus:border-teal focus:ring-2 focus:ring-teal/20 font-body py-5 sm:py-6 text-foreground text-sm"
                    placeholder="john@example.com"
                    required
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="font-body text-xs sm:text-sm text-foreground/70 font-medium mb-2 block">Phone Number</Label>
                  <Input
                    data-testid="checkout-phone"
                    type="tel"
                    inputMode="numeric"
                    value={form.contact_phone}
                    onChange={e => setForm(f => ({ ...f, contact_phone: sanitizeCell(e.target.value) }))}
                    className="rounded-xl bg-muted border-border focus:border-teal focus:ring-2 focus:ring-teal/20 font-body py-5 sm:py-6 text-foreground text-sm"
                    placeholder="+27 82 123 4567"
                    required
                  />
                  <CellphoneHint value={form.contact_phone} />
                </div>
              </div>
            </div>

            {/* Collection Point Selection */}
            <div className="bg-teal/10 rounded-xl sm:rounded-2xl p-5 sm:p-6 border border-teal/20">
              <div className="flex items-center gap-3 mb-3">
                <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-teal" />
                <h3 className="font-heading text-base sm:text-lg font-medium text-foreground">Collection Point</h3>
              </div>
              {collectionPoints.length > 0 ? (
                <div className="space-y-3">
                  <Select
                    value={form.collection_point_id}
                    onValueChange={v => { setForm(f => ({ ...f, collection_point_id: v })); setCpError(false); }}
                  >
                    <SelectTrigger
                      data-testid="collection-point-select"
                      className={`rounded-xl bg-background font-body text-sm py-5 sm:py-6 text-foreground ${cpError ? 'border-destructive ring-1 ring-destructive' : 'border-border'}`}
                    >
                      <SelectValue placeholder="Select a collection point" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      {[...collectionPoints].sort((a,b)=>(a.name||'').localeCompare(b.name||'',undefined,{sensitivity:'base'})).map(cp => (
                        <SelectItem key={cp.id} value={cp.id} className="font-body text-sm">
                          {cp.name} — {cp.address}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {cpError && (
                    <p className="font-body text-xs text-destructive" data-testid="collection-point-error">
                      Please select a collection point to continue.
                    </p>
                  )}
                  {selectedPoint && (
                    <div className="bg-background/50 rounded-lg p-3">
                      <p className="font-body text-sm text-foreground font-medium">{selectedPoint.name}</p>
                      <p className="font-body text-xs text-foreground/60">{selectedPoint.address}</p>
                      {googleMapsLink(selectedPoint.address, selectedPoint.google_maps_url) && (
                        <a
                          href={googleMapsLink(selectedPoint.address, selectedPoint.google_maps_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="checkout-cp-maps-link"
                          className="inline-flex items-center gap-1 font-body text-xs text-teal hover:text-teal/80 underline mt-1"
                        >
                          <MapPin className="h-3 w-3" /> Open in Google Maps
                        </a>
                      )}
                      {selectedPoint.description && (
                        <p className="font-body text-xs text-foreground/40 mt-1">{selectedPoint.description}</p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <p className="font-body text-xs sm:text-sm text-foreground/60">
                  You will be notified with collection details once your order is confirmed.
                </p>
              )}
            </div>

            {/* Payment Section */}
            <div className="bg-card rounded-xl sm:rounded-2xl p-5 sm:p-8 border border-border">
              <h2 className="font-heading text-xl sm:text-2xl font-medium text-foreground mb-3 sm:mb-4 flex items-center gap-3">
                <CreditCard className="h-4 w-4 sm:h-5 sm:w-5 text-teal" />
                Payment
              </h2>

              {afterConfirm ? (
                <div className="p-4 sm:p-5 rounded-xl bg-gold/10 border border-gold/30 mb-2" data-testid="after-confirmation-notice">
                  <p className="font-body text-sm font-semibold text-foreground mb-1">No payment required right now</p>
                  <p className="font-body text-xs sm:text-sm text-foreground/60 leading-relaxed">
                    This supplier confirms orders before payment. Place your order and we&apos;ll review it — once confirmed, you&apos;ll receive a secure payment link by email &amp; WhatsApp.
                  </p>
                </div>
              ) : (
              <>
              {/* Payment method picker — shown only when both methods are enabled */}
              {paymentMethods.card && paymentMethods.eft && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5" data-testid="payment-method-picker">
                  <button
                    type="button"
                    data-testid="select-method-card"
                    onClick={() => setSelectedMethod('card')}
                    aria-pressed={selectedMethod === 'card'}
                    className={`text-left p-4 rounded-xl border-2 transition-all ${
                      selectedMethod === 'card'
                        ? 'bg-teal/15 border-teal ring-2 ring-teal/40 shadow-sm'
                        : 'bg-muted/40 border-border/60 hover:border-teal/40 hover:bg-teal/5'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <CreditCard className="h-4 w-4 text-teal" strokeWidth={2} />
                      <span className="font-body text-sm font-semibold text-foreground">Card</span>
                      {selectedMethod === 'card' && <Check className="h-4 w-4 text-teal ml-auto" strokeWidth={3} />}
                    </div>
                    <p className="font-body text-xs text-foreground/60 leading-relaxed">
                      Pay instantly with Paystack hosted checkout.
                    </p>
                  </button>
                  <button
                    type="button"
                    data-testid="select-method-eft"
                    onClick={() => setSelectedMethod('eft')}
                    aria-pressed={selectedMethod === 'eft'}
                    className={`text-left p-4 rounded-xl border-2 transition-all ${
                      selectedMethod === 'eft'
                        ? 'bg-gold/15 border-gold ring-2 ring-gold/40 shadow-sm'
                        : 'bg-muted/40 border-border/60 hover:border-gold/40 hover:bg-gold/5'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Landmark className="h-4 w-4 text-gold" strokeWidth={2} />
                      <span className="font-body text-sm font-semibold text-foreground">EFT</span>
                      {selectedMethod === 'eft' && <Check className="h-4 w-4 text-gold ml-auto" strokeWidth={3} />}
                    </div>
                    <p className="font-body text-xs text-foreground/60 leading-relaxed">
                      Manual bank transfer. We&apos;ll show banking details next.
                    </p>
                  </button>
                </div>
              )}

              {selectedMethod === 'eft' ? (
                <>
                  <p className="font-body text-xs sm:text-sm text-foreground/60 mb-4 sm:mb-6">
                    Place your order to see banking details and a unique reference. We confirm by email once the funds reflect — usually within 1 business day.
                  </p>
                  <div className="flex items-center gap-3 p-3 sm:p-4 bg-muted rounded-xl mb-4 sm:mb-6">
                    <Landmark className="h-4 w-4 sm:h-5 sm:w-5 text-gold flex-shrink-0" />
                    <p className="font-body text-xs sm:text-sm text-foreground/60">
                      Quote your AVZ reference on the transfer · no card details required.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <p className="font-body text-xs sm:text-sm text-foreground/50 mb-4 sm:mb-6">
                    You&apos;ll be redirected to <span className="font-semibold text-foreground/70">Paystack</span>&apos;s secure checkout to enter your card details. Your order is saved either way — you can finish paying from your <span className="underline">Profile</span> if anything interrupts the redirect.
                  </p>
                  <div className="flex items-center gap-3 p-3 sm:p-4 bg-muted rounded-xl mb-4 sm:mb-6">
                    <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-teal flex-shrink-0" />
                    <p className="font-body text-xs sm:text-sm text-foreground/60">
                      PCI-DSS Level 1 · 3-D Secure · No card data stored on our servers.
                    </p>
                  </div>
                </>
              )}
              </>
              )}

              {hasUnavailable && (
                <div className="mb-4 p-4 rounded-xl bg-destructive/10 border border-destructive/50" data-testid="unavailable-alert">
                  <div className="flex items-start gap-2.5">
                    <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-body text-xs sm:text-sm font-semibold text-foreground">Some items are no longer available</p>
                      <p className="font-body text-[11px] sm:text-xs text-foreground/60 mt-0.5">
                        One or more products in your cart are no longer available. Remove them to continue with the rest of your order.
                      </p>
                      <Button
                        type="button"
                        variant="ctaDanger"
                        size="sm"
                        data-testid="remove-unavailable-btn"
                        onClick={() => {
                          const clearingAll = unavailableIds.length >= items.length;
                          unavailableIds.forEach(id => removeItem(id));
                          if (clearingAll) toast.error('Cart cleared — none of those items are available anymore');
                        }}
                        className="mt-3 rounded-full font-body text-xs"
                      >
                        Remove unavailable item{unavailableIds.length > 1 ? 's' : ''}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                data-testid="pay-now-btn"
                variant="ctaPrimary"
                disabled={loading || windowOpen === false || moqViolations.length > 0 || hasUnavailable}
                className="w-full rounded-xl py-6 sm:py-7 font-body text-sm sm:text-base font-bold transition-all duration-300 hover:-translate-y-0.5 shadow-lg gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    {afterConfirm || selectedMethod === 'eft' ? 'Placing order...' : 'Redirecting to Paystack...'}
                  </>
                ) : windowOpen === false ? (
                  'Order Window Closed'
                ) : fullyCovered ? (
                  <>Place Order — Paid with Credit <ArrowRight className="h-5 w-5" /></>
                ) : afterConfirm ? (
                  <>Place Order — R{money(payable)} <ArrowRight className="h-5 w-5" /></>
                ) : selectedMethod === 'eft' ? (
                  <>Place Order — R{money(payable)} <ArrowRight className="h-5 w-5" /></>
                ) : (
                  <>Pay R{money(payable)} with Paystack <ArrowRight className="h-5 w-5" /></>
                )}
              </Button>
            </div>
          </form>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-card rounded-xl sm:rounded-2xl p-5 sm:p-8 border border-border" data-testid="checkout-summary">
              <h3 className="font-heading text-lg sm:text-xl font-medium text-foreground mb-4 sm:mb-6">Order Summary</h3>

              <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6 max-h-[300px] overflow-y-auto">
                {items.map(item => {
                  const gone = unavailableIds.includes(item.product_id);
                  return (
                  <div key={item.product_id} className={`flex items-center gap-3 sm:gap-4 py-2 sm:py-3 border-b border-border/50 last:border-0 ${gone ? 'rounded-lg border border-destructive/50 px-2.5 -mx-1 my-1' : ''}`} data-testid={`checkout-summary-item-${item.product_id}`}>
                    <div className="flex-1 min-w-0">
                      <p className={`font-body text-xs sm:text-sm font-medium truncate ${gone ? 'text-destructive' : 'text-foreground'}`}>{item.name}</p>
                      {gone ? (
                        <p className="font-body text-[10px] sm:text-xs text-destructive/80" data-testid={`checkout-item-unavailable-${item.product_id}`}>
                          No longer available — please remove
                        </p>
                      ) : (
                        <p className="font-body text-[10px] sm:text-xs text-teal" data-testid={`checkout-item-meta-${item.product_id}`}>
                          Qty: {item.quantity}{formatPack(item) ? ` · ${formatPack(item)}` : ''}
                        </p>
                      )}
                    </div>
                    <p className={`font-body text-xs sm:text-sm font-bold flex-shrink-0 ${gone ? 'text-destructive/60 line-through' : 'text-foreground'}`}>
                      R{money((item.price * item.quantity))}
                    </p>
                  </div>
                  );
                })}
              </div>

              <div className="border-t border-border pt-4 sm:pt-5 space-y-2 sm:space-y-3 font-body text-sm">
                <div className="flex justify-between text-foreground/60 text-xs sm:text-sm">
                  <span>Subtotal ({items.length} items)</span>
                  <span>R{money(total)}</span>
                </div>
                {deliveryChargeLines.length > 1 ? (
                  <div className="space-y-1" data-testid="checkout-delivery-line">
                    <div className="flex justify-between text-foreground/60 text-xs sm:text-sm">
                      <span>Delivery</span>
                      <span>R{money(deliveryCharge)}</span>
                    </div>
                    {deliveryChargeLines.map(l => (
                      <div key={l.supplier_id} className="flex justify-between text-foreground/40 text-[11px] pl-3" data-testid={`checkout-delivery-sub-${l.supplier_id}`}>
                        <span className="truncate mr-2">{l.name}{l.type === 'percentage' ? ` (${l.value}%)` : ''}</span>
                        <span>R{money(l.amount)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex justify-between text-foreground/60 text-xs sm:text-sm" data-testid="checkout-delivery-line">
                    <span>
                      Delivery
                      {deliveryChargeLines[0]?.type === 'percentage' && (
                        <span className="text-foreground/40"> ({deliveryChargeLines[0].value}%)</span>
                      )}
                    </span>
                    <span className={deliveryCharge > 0 ? '' : 'text-foreground/40'}>R{money(deliveryCharge)}</span>
                  </div>
                )}
                <div className="flex justify-between text-foreground/60 text-xs sm:text-sm" data-testid="checkout-collection-fee-line">
                  <span>Collection Fee</span>
                  <span className={collectionFee > 0 ? '' : 'text-foreground/40'}>R{money(collectionFee)}</span>
                </div>
                <div className="flex justify-between text-foreground font-bold text-base sm:text-lg pt-3 border-t border-border">
                  <span>Total</span>
                  <span className="text-gold" data-testid="checkout-total">R{money(grandTotal)}</span>
                </div>
                {creditToApply > 0 && (
                  <>
                    <div className="flex justify-between text-teal text-xs sm:text-sm" data-testid="checkout-credit-line">
                      <span>Account Credit</span>
                      <span>-R{money(creditToApply)}</span>
                    </div>
                    <div className="flex justify-between text-foreground font-bold text-base sm:text-lg pt-2 border-t border-border">
                      <span>Payable Now</span>
                      <span className="text-gold" data-testid="checkout-payable">R{money(payable)}</span>
                    </div>
                  </>
                )}
              </div>

              {/* Fulfillment details — separate from pricing */}
              <div className="border-t border-border mt-4 sm:mt-5 pt-4 sm:pt-5 space-y-2 sm:space-y-3 font-body text-sm" data-testid="checkout-fulfillment-block">
                <div className="flex justify-between text-foreground/60 text-xs sm:text-sm">
                  <span>Fulfillment</span>
                  <span className="text-teal font-semibold">Collection</span>
                </div>
                {selectedPoint && (
                  <div className="flex justify-between text-foreground/60 text-xs sm:text-sm">
                    <span>Collect at</span>
                    <span className="text-foreground/80 font-medium text-right max-w-[60%] truncate">{selectedPoint.name}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
